import { Cart } from '../types/PricingCalculator';
import { formatCurrency } from './pricingLogic';

export function generatePDF(cart: Cart): void {
    const hardwareItems = [
        cart.selections.screen,
        cart.selections.printer,
        cart.selections.scanner,
        ...(cart.selections.adapters || [])
    ].filter(Boolean);

    const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    // Create a new window for PDF generation
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>HOVA AI Ltd - POS System Quote</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Inter', sans-serif;
          line-height: 1.6;
          color: #333;
          background: white;
          padding: 40px;
        }
        
        .header {
          text-align: center;
          margin-bottom: 40px;
          border-bottom: 3px solid #3B82F6;
          padding-bottom: 20px;
        }
        
        .logo {
          font-size: 32px;
          font-weight: 700;
          color: #3B82F6;
          margin-bottom: 10px;
        }
        
        .subtitle {
          font-size: 18px;
          color: #6B7280;
          font-weight: 500;
        }
        
        .quote-info {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
          background: #F8FAFC;
          padding: 20px;
          border-radius: 8px;
        }
        
        .section {
          margin-bottom: 30px;
        }
        
        .section-title {
          font-size: 20px;
          font-weight: 600;
          color: #1F2937;
          margin-bottom: 15px;
          border-left: 4px solid #3B82F6;
          padding-left: 15px;
        }
        
        .config-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 20px;
        }
        
        .config-item {
          background: #F9FAFB;
          padding: 15px;
          border-radius: 6px;
        }
        
        .config-label {
          font-weight: 600;
          color: #374151;
          margin-bottom: 5px;
        }
        
        .config-value {
          color: #6B7280;
        }
        
        .hardware-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          margin-bottom: 10px;
          background: #FAFAFA;
        }
        
        .item-info h4 {
          font-weight: 600;
          color: #1F2937;
          margin-bottom: 5px;
        }
        
        .item-info p {
          color: #6B7280;
          font-size: 14px;
        }
        
        .item-price {
          font-weight: 700;
          color: #3B82F6;
          font-size: 18px;
        }
        
        .pricing-summary {
          background: #F8FAFC;
          padding: 25px;
          border-radius: 8px;
          margin-top: 20px;
        }
        
        .pricing-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #E5E7EB;
        }
        
        .pricing-row:last-child {
          border-bottom: none;
        }
        
        .pricing-row.total {
          font-weight: 700;
          font-size: 18px;
          color: #1F2937;
          border-top: 2px solid #3B82F6;
          margin-top: 10px;
          padding-top: 15px;
        }
        
        .pricing-label {
          color: #374151;
        }
        
        .pricing-value {
          font-weight: 600;
          color: #1F2937;
        }
        
        .footer {
          margin-top: 40px;
          text-align: center;
          color: #6B7280;
          font-size: 14px;
          border-top: 1px solid #E5E7EB;
          padding-top: 20px;
        }
        
        .contact-info {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 20px;
          margin-top: 20px;
        }
        
        .contact-item {
          text-align: center;
        }
        
        .contact-item strong {
          display: block;
          color: #1F2937;
          margin-bottom: 5px;
        }
        
        @media print {
          body { padding: 20px; }
          .header { margin-bottom: 30px; }
          .section { margin-bottom: 25px; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo">HOVA AI Ltd</div>
        <div class="subtitle">POS System Configuration Quote</div>
      </div>
      
      <div class="quote-info">
        <div>
          <strong>Quote Date:</strong> ${currentDate}
        </div>
        <div>
          <strong>Quote ID:</strong> HOVA-${Date.now().toString().slice(-6)}
        </div>
      </div>
      
      <div class="section">
        <div class="section-title">Configuration Summary</div>
        <div class="config-grid">
          <div class="config-item">
            <div class="config-label">Software Eligibility</div>
            <div class="config-value">${cart.eligibility.isFree ? 'Complimentary (RDB Eligible)' : 'Paid Plan Required'}</div>
          </div>
          <div class="config-item">
            <div class="config-label">Device Strategy</div>
            <div class="config-value">${cart.strategy.existingSmartphone ? 'Use existing smartphone' : 'Dedicated POS device'}</div>
          </div>
          <div class="config-item">
            <div class="config-label">Number of Users</div>
            <div class="config-value">${cart.userCount}</div>
          </div>
          ${!cart.eligibility.isFree ? `
          <div class="config-item">
            <div class="config-label">Software Plan</div>
            <div class="config-value">${cart.termMonths} months</div>
          </div>
          ` : ''}
        </div>
      </div>
      
      <div class="section">
        <div class="section-title">Hardware Items</div>
        ${hardwareItems.length > 0 ? hardwareItems.map((item, index) => `
          <div class="hardware-item">
            <div class="item-info">
              <h4>${item?.name || 'Unknown Item'}</h4>
              <p>${item?.description || 'No description available'}</p>
              <p style="font-size: 12px; color: #9CA3AF; margin-top: 5px;">
                ${item?.availability || 'Unknown'} • ${item?.category || 'Unknown'}
              </p>
            </div>
            <div class="item-price">
              ${item ? formatCurrency(item.price.value, item.price.currency) : 'N/A'}
            </div>
          </div>
        `).join('') : '<p style="text-align: center; color: #6B7280; padding: 20px;">No hardware items selected</p>'}
      </div>
      
      <div class="section">
        <div class="section-title">Software Details</div>
        <div class="config-grid">
          <div class="config-item">
            <div class="config-label">Software Name</div>
            <div class="config-value">${cart.selections.software?.name || 'HovaStore POS Software'}</div>
          </div>
          <div class="config-item">
            <div class="config-label">Status</div>
            <div class="config-value">${cart.eligibility.isFree ? 'Complimentary (RDB eligible)' : 'Paid subscription'}</div>
          </div>
          <div class="config-item">
            <div class="config-label">Plan Duration</div>
            <div class="config-value">${cart.eligibility.isFree ? 'Free' : `${cart.termMonths} months`}</div>
          </div>
          ${cart.totals.additionalUsers.value > 0 ? `
          <div class="config-item">
            <div class="config-label">Additional Users</div>
            <div class="config-value">${cart.userCount - 2} users (5,000 RWF each)</div>
          </div>
          ` : ''}
        </div>
      </div>
      
      <div class="pricing-summary">
        <div class="section-title">Pricing Summary</div>
        <div class="pricing-row">
          <span class="pricing-label">Hardware Total:</span>
          <span class="pricing-value">${formatCurrency(cart.totals.hardware.value, 'RWF')}</span>
        </div>
        <div class="pricing-row">
          <span class="pricing-label">Software Total:</span>
          <span class="pricing-value">${formatCurrency(cart.totals.software.value, 'RWF')}</span>
        </div>
        ${cart.totals.additionalUsers.value > 0 ? `
        <div class="pricing-row">
          <span class="pricing-label">Additional Users (${cart.userCount - 2} users):</span>
          <span class="pricing-value">${formatCurrency(cart.totals.additionalUsers.value, 'RWF')}</span>
        </div>
        ` : ''}
        ${!cart.eligibility.isFree && cart.totals.discountRate ? `
        <div class="pricing-row">
          <span class="pricing-label">Software Discount:</span>
          <span class="pricing-value">-${Math.round(cart.totals.discountRate * 100)}%</span>
        </div>
        ` : ''}
        <div class="pricing-row total">
          <span class="pricing-label">Grand Total:</span>
          <span class="pricing-value">${formatCurrency(cart.totals.grandTotal.value, 'RWF')}</span>
        </div>
      </div>
      
      <div class="footer">
        <div class="contact-info">
          <div class="contact-item">
            <strong>Email</strong>
            info@hova.ai
          </div>
          <div class="contact-item">
            <strong>Phone</strong>
            +250 709 050 000
          </div>
          <div class="contact-item">
            <strong>Website</strong>
            www.hova.ai
          </div>
        </div>
        <p style="margin-top: 20px;">
          Thank you for choosing HOVA AI Ltd!<br>
          This quote is valid for 30 days from the date of issue.
        </p>
      </div>
    </body>
    </html>
  `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    // Wait for content to load, then trigger print
    printWindow.onload = () => {
        setTimeout(() => {
            printWindow.print();
        }, 500);
    };
}
